import { Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AumicoCoreProvider, IAumMenuOptionsForRow, ITableOptions, NotificationType } from '@modeso/aumico-lib-core-fe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import Debug from 'debug';
import { BehaviorSubject, skip } from 'rxjs';
import { userManagement } from '../../actions/users.management.actions';
import { UserModificationConfirmationModal } from '../../components/user-modification-confirmation-modal/user-modification-confirmation-modal.component';
import { localizationKeys } from '../../constants/localizationKeys';
import { Role } from '../../enums/role.enum';
import { UserManagementListMenuActions } from '../../enums/users-management-list-menu-actions.enum';
import { IUserManagementDialogData } from '../../interfaces/user-management-dialog-data.interface';
import { IUser } from '../../interfaces/user.interface';
import { UsersManagementProvider } from '../../provider/users.management.provider';

const debug = Debug('aumico:users:UsersListPage');

@UntilDestroy()
@Component({
    selector: 'aumico-users-list-page',
    templateUrl: './users-list.page.html',
    styleUrls: ['./users-list.page.scss'],
})
export class UsersListPage implements OnInit {
    options: ITableOptions;
    dataLoaded: boolean;
    data: IUser[];

    @Input() onFilterEvent = new BehaviorSubject<string>(undefined);

    constructor(
        private readonly usersStoreProvider: UsersManagementProvider,
        private readonly dialog: MatDialog,
        private readonly actionListener$: ActionsSubject,
        private readonly coreProvider: AumicoCoreProvider,
    ) {
        this.options = {
            columns: [
                { name: 'email', type: 'text', property: 'email' },
                { name: 'organization', type: 'text', property: 'organization' },
                { name: 'organization role', type: 'text', property: 'organizationRole' },
                { name: 'technical roles', type: 'array', property: 'roles' },
                { name: 'organization state', type: 'text', property: 'state' },
                { name: 'user activity', type: 'active', property: 'active' },
                { name: 'registartion', type: 'date', property: 'createdAt' },
                { name: 'actions' },
            ],
            menuPerRow: this.getMenuOptions(),
        };
    }
    onMenuItemPerRowClick(action: string, user: IUser): void {
        let data: IUserManagementDialogData;

        switch (action) {
            case UserManagementListMenuActions.BLOCK:
                data = {
                    titleKey: 'aumico_admin_usermanagement_confirmation_modal_title_block_user',
                    descriptionKey: 'aumico_admin_usermanagement_confirmation_modal_change_user_activity_state',
                    buttonTextKey: 'aumico_admin_usermanagement_confirmation_modal_confirm_button',
                    action: () => this.blockUser(user),
                    showCancel: true,
                    icon: '',
                    currentState: 'Active',
                    newState: 'In active',
                    valueToReplace: user.email,
                };
                break;
            case UserManagementListMenuActions.UNBLOCK:
                data = {
                    titleKey: 'aumico_admin_usermanagement_confirmation_modal_title_unblock_user',
                    descriptionKey: 'aumico_admin_usermanagement_confirmation_modal_change_user_activity_state',
                    buttonTextKey: 'aumico_admin_usermanagement_confirmation_modal_confirm_button',
                    action: () => this.unBlock(user),
                    showCancel: true,
                    icon: '',
                    currentState: 'Inactive',
                    newState: 'Active',
                    valueToReplace: user.email,
                };
                break;
            case UserManagementListMenuActions.PROMOTE_TO_ADMIN:
                data = {
                    titleKey: 'aumico_admin_usermanagement_confirmation_modal_title_promote_admin',
                    descriptionKey: 'aumico_admin_usermanagement_confirmation_modal_change_user_role',
                    buttonTextKey: 'aumico_admin_usermanagement_confirmation_modal_confirm_button',
                    action: () => this.promoteUserToAdmin(user),
                    showCancel: true,
                    icon: '',
                    currentState: user.roles.join(', '),
                    newState: [...user.roles, Role.APP_ADMIN].join(', '),
                    valueToReplace: user.email,
                };
                break;

            case UserManagementListMenuActions.DEMOTE_ADMIN:
                data = {
                    titleKey: 'aumico_admin_usermanagement_confirmation_modal_title_demote_admin',
                    descriptionKey: 'aumico_admin_usermanagement_confirmation_modal_change_user_role',
                    buttonTextKey: 'aumico_admin_usermanagement_confirmation_modal_confirm_button',
                    action: () => this.demoteUser(user),
                    showCancel: true,
                    icon: '',
                    currentState: user.roles.join(', '),
                    newState: [...user.roles].filter((role) => role !== Role.APP_ADMIN).join(', '),
                    valueToReplace: user.email,
                };
                break;
        }
        this.openUserManagementConfirmationDialog(data);
    }

    ngOnInit(): void {
        this.usersStoreProvider.dispatchGetUsers();
        this.usersStoreProvider
            .getAllUsers$()
            .pipe(skip(1), untilDestroyed(this))
            .subscribe((users: IUser[]) => this.handleGetUsers(users));
        this.actionListener$
            .pipe(
                ofType(
                    userManagement.unblockUserSuccess,
                    userManagement.blockUserSuccess,
                    userManagement.demoteUserFromAdminSuccess,
                    userManagement.promoteUserToAdminSuccess,
                ),
                untilDestroyed(this),
            )
            .subscribe(() => this.handleSuccess());
        this.actionListener$
            .pipe(
                ofType(
                    userManagement.unblockUserFailure,
                    userManagement.blockUserFailure,
                    userManagement.demoteUserFromAdminFailure,
                    userManagement.promoteUserToAdminFailure,
                ),
                untilDestroyed(this),
            )
            .subscribe(() => this.handleError());
    }
    private handleSuccess(): void {
        this.coreProvider.dispatchAddNotification(
            localizationKeys.aumico_admin_user_list_notification_success_title,
            localizationKeys.aumico_admin_user_list_notification_success_description,
            NotificationType.SUCCESS,
        );
    }
    private handleError(): void {
        this.coreProvider.dispatchAddNotification(
            localizationKeys.aumico_admin_user_list_notification_failure_title,
            localizationKeys.aumico_admin_user_list_notification_failure_description,
            NotificationType.ERROR,
        );
    }

    private handleGetUsers(users: IUser[]): void {
        if (users != null && users.length > 0) {
            this.data = [...users];
            this.dataLoaded = true;
        }
    }

    private getMenuOptions(): IAumMenuOptionsForRow {
        return {
            items: [
                {
                    interchangeable: true,
                    conditionalOptions: [
                        {
                            propertyKey: 'active',
                            value: 'false',
                            caption: 'aumico_admin_menu_item_unblock_user',
                            icon: 'lock_open',
                            action: UserManagementListMenuActions.UNBLOCK,
                        },
                        {
                            propertyKey: 'active',
                            value: 'true',
                            caption: 'aumico_admin_menu_item_block_user',
                            icon: 'lock',
                            action: UserManagementListMenuActions.BLOCK,
                        },
                        {
                            propertyKey: 'roles',
                            value: [Role.ORG_USER, Role.ORG_ADMIN],
                            caption: 'aumico_admin_menu_item_promote_to_admin',
                            icon: 'verified_user',
                            action: UserManagementListMenuActions.PROMOTE_TO_ADMIN,
                        },
                        {
                            propertyKey: 'roles',
                            value: [Role.ORG_ADMIN, Role.ORG_USER, Role.APP_ADMIN],
                            caption: 'aumico_admin_menu_item_demote_to_admin',
                            icon: 'verified_user',
                            action: UserManagementListMenuActions.DEMOTE_ADMIN,
                        },
                    ],
                },
            ],
        };
    }

    private unBlock(user: IUser): void {
        this.usersStoreProvider.dispatchUnBlockUser(user.id);
    }
    private blockUser(user: IUser): void {
        this.usersStoreProvider.dispatchBlockUser(user.id);
    }
    private promoteUserToAdmin(user: IUser): void {
        this.usersStoreProvider.dispatchPromoteToAdmin(user.id);
    }
    private demoteUser(user: IUser): void {
        this.usersStoreProvider.dispatchDemoteToAdmin(user.id);
    }
    private openUserManagementConfirmationDialog(data: IUserManagementDialogData): void {
        this.dialog.open(UserModificationConfirmationModal, {
            data,
        });
    }
}
